import React, { memo, useCallback, useEffect, useState } from 'react'
import styled from 'styled-components/macro'

import { CloseIcon } from '../../components/Icons'
import { EmptyState, ModalBody, ModalHeader, ModalWrapper, SectionTitle, tokens } from '../../components/ui'
import BASE_API from '../../constants/baseApi'
import AUTH_TOKEN from '../../constants/authToken'

/*
 * Endereços da clínica.
 *
 * O vínculo clínica ↔ endereço tinha sido perdido (migration 0056 apagou a
 * tabela UserAddress) e este modal ficou comentado no Dashboard. Com o
 * endpoint /establishmentAddress de volta, o cadastro acontece aqui.
 *
 * O preenchimento vem do CEP (ViaCEP): o usuário digita 8 dígitos e só
 * completa número e complemento.
 */

const ADDRESS_URL = `${BASE_API}/establishmentAddress`

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem(AUTH_TOKEN)}`
})

const EMPTY_FORM = {
  id: null,
  name: '',
  postal_code: '',
  street: '',
  number: '',
  complement: '',
  district: '',
  city_name: '',
  uf: '',
  reference: '',
  latitude: '',
  longitude: '',
  is_main: false
}

const maskCep = value => {
  const digits = (value || '').replace(/\D/g, '').slice(0, 8)
  return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits
}

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
`

const FieldWrap = styled.div`
  ${props => props.$full && 'grid-column: 1 / -1;'}
`

const Label = styled.label`
  display: block;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${tokens.label};
  margin-bottom: 4px;
`

const Input = styled.input`
  width: 100%;
  height: 40px;
  padding: 0 12px;
  font-size: 14px;
  color: ${tokens.text};
  background: #fff;
  border: 1px solid ${tokens.border};
  border-radius: 8px;
  outline: none;
  &:focus { border-color: ${tokens.brand}; }
  &:disabled { background: ${tokens.surfaceSoft}; color: ${tokens.textSoft}; }
`

const Button = styled.button`
  height: 40px;
  padding: 0 18px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 8px;
  border: 1px solid ${props => (props.$ghost ? tokens.border : tokens.brand)};
  background: ${props => (props.$ghost ? '#fff' : tokens.brand)};
  color: ${props => (props.$ghost ? tokens.textSoft : '#fff')};
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: default; }
`

const LinkButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 13px;
  font-weight: 600;
  color: ${props => (props.$danger ? '#b3261e' : tokens.brand)};
  cursor: pointer;
`

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 0;
  border-top: 1px solid ${tokens.borderSoft};
  &:first-child { border-top: none; }
`

const MainTag = styled.span`
  display: inline-block;
  margin-left: 8px;
  padding: 2px 9px;
  border-radius: 999px;
  background: #e6f6ec;
  color: #1d7a45;
  font-size: 11px;
  font-weight: 600;
`

const Message = styled.div`
  margin: 12px 0 0;
  font-size: 13px;
  color: ${props => (props.$error ? '#b3261e' : '#1d7a45')};
`

const Addresses = memo(({ establishmentId, onRequestClose, onUpdated }) => {
  const [addresses, setAddresses] = useState([])
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState(EMPTY_FORM)
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [cepLoading, setCepLoading] = useState(false)
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const response = await fetch(`${ADDRESS_URL}?establishment=${establishmentId}`, { headers: authHeaders() })
      if (!response.ok) throw new Error('Não foi possível carregar os endereços.')
      setAddresses(await response.json())
      setError(null)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [establishmentId])

  useEffect(() => { load() }, [load])

  const setField = (field, value) => setForm(prev => ({ ...prev, [field]: value }))

  /* Busca no ViaCEP assim que o CEP fica completo. */
  const handleCep = async value => {
    const masked = maskCep(value)
    setField('postal_code', masked)
    const digits = masked.replace(/\D/g, '')
    if (digits.length !== 8) return

    setCepLoading(true)
    try {
      const response = await fetch(`https://viacep.com.br/ws/${digits}/json/`)
      const data = await response.json()
      if (data.erro) {
        setError('CEP não encontrado. Preencha os campos manualmente.')
        return
      }
      setForm(prev => ({
        ...prev,
        postal_code: masked,
        street: data.logradouro || prev.street,
        district: data.bairro || prev.district,
        city_name: data.localidade || prev.city_name,
        uf: data.uf || prev.uf,
        complement: prev.complement || data.complemento || ''
      }))
      setError(null)
    } catch {
      setError('Não foi possível consultar o CEP agora. Preencha manualmente.')
    } finally {
      setCepLoading(false)
    }
  }

  const openNew = () => { setForm(EMPTY_FORM); setShowForm(true); setError(null) }

  const openEdit = address => {
    setForm({
      id: address.id,
      name: address.name || '',
      postal_code: address.postal_code || '',
      street: address.street || '',
      number: address.number || '',
      complement: address.complement || '',
      district: address.district || '',
      city_name: (address.city && address.city.name) || address.city_name || '',
      uf: (address.city && address.city.uf && address.city.uf.abbreviation) || address.uf || '',
      reference: address.reference || '',
      latitude: address.latitude || '',
      longitude: address.longitude || '',
      is_main: !!address.is_main
    })
    setShowForm(true)
    setError(null)
  }

  const save = async () => {
    if (!form.postal_code.trim()) { setError('Informe o CEP.'); return }
    if (!form.street.trim()) { setError('Informe o logradouro.'); return }

    setSaving(true)
    try {
      const body = { ...form, establishment: establishmentId }
      delete body.id
      const url = form.id ? `${ADDRESS_URL}?id=${form.id}` : ADDRESS_URL
      const response = await fetch(url, {
        method: form.id ? 'PUT' : 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body)
      })
      if (!response.ok) {
        const data = await response.json().catch(() => null)
        throw new Error((data && (data.detail || data.error)) || 'Não foi possível salvar o endereço.')
      }
      setShowForm(false)
      setForm(EMPTY_FORM)
      await load()
      if (onUpdated) onUpdated()
      setError(null)
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const remove = async address => {
    if (!window.confirm('Excluir este endereço?')) return
    try {
      const response = await fetch(`${ADDRESS_URL}?id=${address.id}`, { method: 'DELETE', headers: authHeaders() })
      if (!response.ok) throw new Error('Não foi possível excluir o endereço.')
      await load()
      if (onUpdated) onUpdated()
    } catch (err) {
      setError(err.message)
    }
  }

  return (
    <ModalWrapper>
      <ModalHeader title='Endereços' onRequestClose={onRequestClose} closeIcon={<CloseIcon />} />
      <ModalBody>
        {!showForm && (
          <>
            <SectionTitle>Cadastrados</SectionTitle>
            {loading && <EmptyState>Carregando...</EmptyState>}
            {!loading && addresses.length === 0 && <EmptyState>Nenhum endereço cadastrado</EmptyState>}
            {!loading && addresses.map(address => (
              <Row key={address.id}>
                <div>
                  <div style={{ fontWeight: 600, color: tokens.text }}>
                    {address.name || 'Endereço'}
                    {address.is_main && <MainTag>principal</MainTag>}
                  </div>
                  <div style={{ fontSize: 14, color: tokens.textSoft, marginTop: 2 }}>
                    {address.full_address}
                  </div>
                  {address.reference && (
                    <div style={{ fontSize: 13, color: tokens.label, marginTop: 2 }}>
                      Referência: {address.reference}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 14, whiteSpace: 'nowrap' }}>
                  <LinkButton onClick={() => openEdit(address)}>Editar</LinkButton>
                  <LinkButton $danger onClick={() => remove(address)}>Excluir</LinkButton>
                </div>
              </Row>
            ))}
            <div style={{ marginTop: 20 }}>
              <Button onClick={openNew}>Adicionar endereço</Button>
            </div>
          </>
        )}

        {showForm && (
          <>
            <SectionTitle>{form.id ? 'Editar endereço' : 'Novo endereço'}</SectionTitle>
            <Grid>
              <FieldWrap>
                <Label>CEP</Label>
                <Input
                  value={form.postal_code}
                  onChange={e => handleCep(e.target.value)}
                  placeholder='00000-000'
                  inputMode='numeric'
                />
                {cepLoading && <div style={{ fontSize: 12, color: tokens.label, marginTop: 4 }}>Buscando CEP...</div>}
              </FieldWrap>

              <FieldWrap>
                <Label>Apelido da unidade</Label>
                <Input value={form.name} onChange={e => setField('name', e.target.value)} placeholder='Ex.: Unidade Centro' />
              </FieldWrap>

              <FieldWrap $full>
                <Label>Logradouro</Label>
                <Input value={form.street} onChange={e => setField('street', e.target.value)} />
              </FieldWrap>

              <FieldWrap>
                <Label>Número</Label>
                <Input value={form.number} onChange={e => setField('number', e.target.value)} placeholder='S/N' />
              </FieldWrap>

              <FieldWrap>
                <Label>Complemento</Label>
                <Input value={form.complement} onChange={e => setField('complement', e.target.value)} />
              </FieldWrap>

              <FieldWrap>
                <Label>Bairro</Label>
                <Input value={form.district} onChange={e => setField('district', e.target.value)} />
              </FieldWrap>

              <FieldWrap>
                <Label>Cidade</Label>
                <Input value={form.city_name} onChange={e => setField('city_name', e.target.value)} />
              </FieldWrap>

              <FieldWrap>
                <Label>UF</Label>
                <Input
                  value={form.uf}
                  onChange={e => setField('uf', e.target.value.toUpperCase().slice(0, 2))}
                  maxLength={2}
                />
              </FieldWrap>

              <FieldWrap $full>
                <Label>Ponto de referência</Label>
                <Input value={form.reference} onChange={e => setField('reference', e.target.value)} />
              </FieldWrap>

              <FieldWrap>
                <Label>Latitude (opcional)</Label>
                <Input value={form.latitude} onChange={e => setField('latitude', e.target.value)} placeholder='-22.9068' />
              </FieldWrap>

              <FieldWrap>
                <Label>Longitude (opcional)</Label>
                <Input value={form.longitude} onChange={e => setField('longitude', e.target.value)} placeholder='-43.1729' />
              </FieldWrap>

              <FieldWrap $full>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: tokens.text }}>
                  <input
                    type='checkbox'
                    checked={form.is_main}
                    onChange={e => setField('is_main', e.target.checked)}
                  />
                  Endereço principal
                </label>
              </FieldWrap>
            </Grid>

            {error && <Message $error>{error}</Message>}

            <div style={{ display: 'flex', gap: 12, marginTop: 22 }}>
              <Button onClick={save} disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</Button>
              <Button $ghost onClick={() => { setShowForm(false); setError(null) }} disabled={saving}>Cancelar</Button>
            </div>
          </>
        )}

        {!showForm && error && <Message $error>{error}</Message>}
      </ModalBody>
    </ModalWrapper>
  )
})

export default Addresses
